/* eslint-disable no-shadow */
// 首页相关接口
import { api } from '@/utils/axios'
import { AuthResponese, PartnerProtocolList, ProductMatchInfo, ProductNextMatchDTO, Protocols, UserInfo } from '@/types/apis/user'
import { ApiResponse, EntranceType } from '@/types/apis'
import { ApplyLimit, ApplyStatus, AssetSituation, BumpStatus, CarProperty, Credit, CreditInvestigation, Gender, HouseProperty, HousingFund, Insurance, Job, MonthlyProfit, PayoffForm, RematchResult, SesameCredit, SocialSecurity, SubmitStatus, WorkDuration } from '@/constants/product-enum'
import { APPH5, ForceDistributeStatus, OneStageStatus, SignFieldFlag, UnionLogin, YesNo } from '@/constants/user'
import { CompanyType, FqlPhoneModel } from '@/constants/common'
import { LoginUser, ReportProductExposureParams } from './activity'
import { decoratorAsyncCache } from '@/utils/decorator/asyncCache'
import { useAdChannelStore } from '@/store/adChannelCode.store'
import { DEFAULT_DOMAIN_BRANDING, getDomainBrandingByHostname } from '@/constants/domain-branding'
import { RouteMode } from '@/constants/channel'
import { omitInvalidRealName } from '@/utils/real-name'

const getAgreementAppNameByHostname = () => {
  const channelValue = useAdChannelStore.getState().channelValue
  if (typeof window === 'undefined') {
    return DEFAULT_DOMAIN_BRANDING.appName
  }
  if (channelValue.includes('daixiaoyouTF')) {
    return '贷小悠'
  }

  return getDomainBrandingByHostname(window.location.hostname).appName
}

const getAgreementCompanyByHostname = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_DOMAIN_BRANDING.companyName
  }

  return getDomainBrandingByHostname(window.location.hostname).companyName
}

const transformAgreementContent = (content: string) => {
  const channelValue = useAdChannelStore.getState().channelValue
  const domainCompanyName = getAgreementCompanyByHostname()
  let finalCompanyName = domainCompanyName

  switch (true) {
    case channelValue.includes('jiezhanggui'):
      finalCompanyName = '本平台'
      break
    case channelValue.includes('xiangJiangYuan'):
      finalCompanyName = CompanyType.XJY_COMPANY.company
      break
    case channelValue.includes('zhonganXy'):
      finalCompanyName = CompanyType.ZAXY_COMPANY.company
      break
    default:
      finalCompanyName = domainCompanyName
  }

  let nextContent = content.replace(/有钱钱包/g, getAgreementAppNameByHostname())
  nextContent = nextContent.replace(/成都乐花数字科技有限公司/g, finalCompanyName)

  return nextContent
}

/** 表单字段 */
interface FormFields {

  /**
   * 真实姓名
   */
  realName?: string | null;

  /**
   * 身份证号
   */
  identity?: string | null;

  /**
   * 年龄
   */
  age?: number | null;

  /**
   * 性别，1-男、2-女
   */
  gender?: Gender | null;

  /**
   * 工作城市
   */
  workCity?: string | null;

  /**
   * 工作城市地区编码
   */
  workCityCode?: string | null;

  /**
   * 职业身份
   */
  job?: Job | null;

  /**
   * 工作年限
   */
  workDuration?: WorkDuration | null;

  /**
   * 工资发放
   */
  payoffForm?: PayoffForm | null;

  /**
   * 月收入
   */
  monthlyProfit?: MonthlyProfit | null;

  /**
   * 芝麻信用
   */
  sesameCredit?: SesameCredit | null;

  /**
   * 申请额度
   */
  applyLimit?: ApplyLimit | null;

  /**
   * 房产
   */
  houseProperty?: HouseProperty | null;

  /**
   * 车产
   */
  carProperty?: CarProperty | null;

  /**
   * 社保缴纳
   */
  socialSecurity?: SocialSecurity | null;

  /**
   * 公积金
   */
  housingFund?: HousingFund | null;

  /**
   * 保险保单
   */
  insurance?: Insurance | null;

  /**
   * 征信情况
   */
  creditInvestigation?: CreditInvestigation | null;
}

/** 保存客户信息（APP、H5都会调用） */
export interface SaveParams extends FormFields {

  /**
   * 用户渠道 code
   */
  adChannelCode: string;

  /**
   * 资产情况
   * H5 不再使用，但是 APP 还在使用
   */
  assetSituation?: AssetSituation[] | null;

  /**
   * 征信情况（详细版）
   * H5 不再使用，但是 APP 还在使用
   */
  credit?: Credit[] | null;

  /**
   * 是否华为手机
   */
  isHuaWeiPhone?: boolean;

  /**
   * 分期乐手机型号
   */
  phoneModel?:FqlPhoneModel
}

/** 临时保存客户信息 */
export interface TempSaveParams extends FormFields { }

/** 用户直接匹配参数 */
export interface DirectMatchInfo {
  adChannelCode: string,
  directMatchType: number

  /** 分期乐手机型号 */
  phoneModel?:FqlPhoneModel
}

/** 人脸识别返回参数 */
interface FaceVerifyResponse{

  /** 实人认证唯一标识 */
  certifyId: string,

  /** Web认证URL */
  certifyUrl: string
}

interface LoginInfo {
  uid: string,
  realName: string,
  mobile: string,
  loginAt:string
}

/** 获取用户表单流程中途推出后的信息获取 */
export interface GetProductInfoWidthStatus extends ProductMatchInfo {

  /** 是否提交表单，0-未提交，1-提交表单未授权，10-授权已分发 */
  submitStatus: SubmitStatus | null,

  /** 申请额度，1-0万3万、2-3万5万、3-5万10万、4-10万20万、5-20万以上 */
  applyLimit?:ApplyLimit

  /** 工作城市 */
  workCity: string

  /** 重新匹配结果 */
  rematchResult: RematchResult | null

  /**  是否是低产流量 */
  lowFlow:boolean
}

/** 联合类型用户匹配信息 */
export type RqResult = GetProductInfoWidthStatus | ProductNextMatchDTO | ProductMatchInfo

/** 二要素校验 */
export interface IdentityNameVerify {

  /** 身份证号码 */
  identity: string | null

  /** 真实姓名 */
  realName: string | null

  /** 渠道码 */
  adChannelCode:string | null
}

/** 获取协议内容*/
export interface AgreementInfoResponse {

  /** 协议内容*/
  content: string,

  /** 是否填充0-不填充，1-填充*/
  signFieldFlag:SignFieldFlag
}

export interface AgreementInfoOptions {
  isMainProtocol?: boolean
}

/** 发起产品申请-请求 */
export interface ApplyParams {

  /** 产品id*/
  productId: number,

  /** 入口类型，1-首页中插，2-首页弹窗、3-开屏、4-顶部推荐、5-首页列表、6-产品列表、7-随机推荐*/
  entranceType: EntranceType,

   /** 请求来源是APP还是H5 1-有钱来APP、2-普通H5来源、4-有钱助手来源、5-H5挽留来源*/
  appH5: APPH5,

  /** 流水号 */
  serialNo?:string | number
}

/** 有钱来API下游VO */
export interface YqlDownstreamApiApplyVO {

  /** 撞库状态码*/
  bumpStatus: BumpStatus,

  /** 全流程产品名*/
  partnerProductName: string

  /** 全流程公司主体名*/
  partnerCompanyName:string

  /** 全流程下游产品logo URL*/
  partnerLogo: string

  /** 下游的协议列表L*/
  partnerProtocolList: PartnerProtocolList[]

  /** 产品价格*/
  productPrice:number
}

/** 发起产品申请-响应 */
export interface ApplyResponse {

  /** 申请状态*/
  applyStatus: ApplyStatus,

  /** 第三方跳转目标地址*/
  thirdpartyTargetUrl: string,

  /** h5联登产品协议列表*/
  h5Protocols: Protocols

  /** 是否联登（H5产品），0->否，1->是*/
  unionLoginFlag: UnionLogin

  /** 消息提示*/
  msg: string

  /** 是否是强制分发*/
  isForceDistribute: ForceDistributeStatus

  /** 是否属于一段结算的Api转h 5*/
  isOneStage: OneStageStatus

  /** 是否表单映射*/
  formMapping:boolean

  /** 有钱来API下游VO*/
  yqlDownstreamApiApplyVO:YqlDownstreamApiApplyVO
}

/** 获取协议内容 2.0-请求参数 */
export interface getProtocolContentReq {

  /** 协议id */
  protocolId?:string

  /** 产品 id */
  productId?:number

  /** 脱敏名字 */
  maskName?:string

  /** 脱敏手机号 */
  maskMobile?:string

  /** 城市 */
  workCity?:string

  /** 流水号 */
  serialNo?: string | null
}

export interface getAdChannelCodeByRouteCodeRes {//
  /** 渠道码 */
  adChannelCode:string//
  /** 是否绑定产品计划 0否1是 */
  flgBindPlan:number//
  /** 产品计划目标地址 */
  planTargetUrl: string | null //
  /** 是否开启路由码 1开启 0关闭 */
  flgOnline: YesNo
  /** 是否开启路由码联登功能 1 开启 0 关闭 */
  flgUnion: YesNo
  /** 导流的产品id */
  productId?: number
  /** crm跳转企业微信地址 */
  crmJumpUrl: string | null
  /** 导下游联登产品名称 */
  productName: string | null
  /** 路由模式 */
  routeMode: RouteMode
  /** 时间段导流当前是否生效 0否 1是 */
  timePeriodEffectiveFlag: YesNo
}
/** 顶部轮播图推荐产品 */
export interface TopRecommentdProductItem {
  /** 主键 */
  id: number;
  /** 产品名称 */
  productName: string;
  /** 产品外显名称 */
  productNickName: string;
  /** 产品logo */
  productLogo: string;
  /** 借款额度下限 */
  loansLimitMin: number;
  /** 借款额度上限 */
  loansLimitMax: number;
  /** 月利率 */
  monthlyInterestRate: number;
  /** 是否展示详情页 */
  showDetailPage: number;
  /** 是否首页顶部推荐 */
  showHomePageTop: number;
  /** 首页顶部推荐权重值 */
  showHomePageTopWeight: number;
  /** 创建时间 */
  createdAt: number;
  /** 是否联登 */
  unionLoginFlag: UnionLogin;
  /** 申请率 */
  applyRate: number;
  /** 流水号 */
  serialNo: string
  /** 随机申请利率 */
  lendingRateString?: number | null
  /** 产品计划id */
  planCode?:string
}

/** 首页列表 */
export interface ColumnRecomenedProductList {
  /** 今日爆款推荐 */
  hotRecommends:TopRecommentdProductItem[],
  /** 小额借的到推荐 */
  microfinanceRecommends: TopRecommentdProductItem[]
  /** 大额低息推荐 */
  jumboLoanRecommends: TopRecommentdProductItem[]
  /** 最高价推荐 */
  firstRecommends:TopRecommentdProductItem
}

export interface H5ProductUnionLoginReq extends ApplyParams {
  /** 联登的H5产品 */
  productId: number
  /** 是否点击授权 */
  agree?:boolean
}

/** 上报CRM数据 */
export interface ReportCrmDataReq {
  /** 手机号 */
  mobile:string,
  /** 产品id */
  productId?:string | number | null,
  /** 匿名id */
  anonymousId:string | null
  /** 渠道码 */
  adChannelCode:string
}

/** 查询协议 - 请求参数 */
export interface GetByProtocolTypeAndLinkShowReq {
  /** 协议类型 */
  protocolType: number
  /** 协议展示环节 */
  linkShow:number
  /** 包名类型 默认为有钱钱包 - 1 */
  packageType:number
}

/** 查询协议 - 响应参数 */
export interface GetByProtocolTypeAndLinkShowRes {
  /** 协议内容 */
  content: string
  /** 创建时间 */
  createdAt:number
  /** id */
  id:number
  /** 展示环节 */
  linkShow:number
  /** 包名类型：1-有钱钱包 */
  packageType:number
  /** 产品id */
  productId:number
  /** 协议id */
  protocolCode:string
  /** 协议名称 */
  protocolName:string
  /** 协议类型 */
  protocolType:number
  /** 是否回填 */
  signFieldFlag:number
  /** 回填内容 */
  signFields:string[]
  /** 更新时间 */
  updatedAt:number
}

/** 客户信息新增 - 请求参数 */
export interface CrmAddCustomerReq {
  /** 手机号 */
  mobile:string
  /** 渠道码 */
  adChannelCode:string
  /** 姓名 */
  name:string
  /** 线下门店 */
  offlineStore:string
  /** 公司名称 */
  companyName:string
  /** 城市名称 */
  cityName:string
  /** 城市code */
  cityCode:string
}

/** 登录后获取时间段导流最高价产品 - 请求参数 */
export interface timePeriodDirectDrainageProductReq {
  /** 路由码 */
  adRouteCode: string
  /** 渠道码 */
  adChannelCode: string
}

/** 登录后获取时间段导流最高价产品 - 响应参数 */
export interface timePeriodDirectDrainageProductRes {
  /** 产品计划目标地址 */
  targetUrl: string | null
  /** 导流的产品 id */
  productId?: number
  /** 导流的产品名称 */
  productName: string | null
}


const indexApi = {
  // 保存客户申请信息
  saveApply(body: SaveParams) {
    return api.post('/api/user/info/save', body, {
      // mock: true
    })
  },

  // 暂存客户信息
  tempSave(body: TempSaveParams) {
    return api.post('/api/user/info/tempSave', omitInvalidRealName(body), {
      // mock: true
    })
  },

  // 二要素校验(身份证、姓名)
  identityNameVerify(body: IdentityNameVerify) {
    return api.post('/api/user/info/identityNameVerify', body, {
      // mock: true
    })
  },

  // 保存客户信息
  saveCustomerInfo(body: SaveParams) {
    return api.post<ProductMatchInfo>('/api/user/info/save', body, {
      // mock: true
    })
  },

  //大联登用户直接匹配
  directMatchInfo(body:DirectMatchInfo) {
    return api.post<GetProductInfoWidthStatus>('/api/user/info/direct/match', body, {
      //mock: true
      isCancelRequest:false
    })
  },

  // 发起人脸身份识别
  faceVerify(body = {}) {
    return api.post<FaceVerifyResponse>('/api/user/info/faceVerify', body, {
      // mock: true
    })
  },

  //获取用户资料
  getApplyStatus(body = {}) {
    return api.get<ApiResponse<UserInfo>>('/api/user/info/detail', body, {
      // mock: true,
      showError: false,
      withoutCheck: true
    })
  },

  // 获取当前登录用户信息
  getLoginUserInfo(body = {}) {
    return api.get<ApiResponse<LoginUser>>('/api/getLoginUserInfo', body, {
      // mock: true,
      withoutCheck: true
    })
  },

  // 获取用户表单流程中途推出后的信息获取
  getFomrSubmitStatus(body = {}) {
    return api.get<GetProductInfoWidthStatus>('/api/user/info/status/result', body, {
      // mock: true,
    })
  },

  // 认证接口
  confirmAuthorize(body = {}) {
    return api.put<AuthResponese>('/api/user/info/auth', body, {
      // mock: true,
    })
  },

  // 获取需要隐藏的表单项
  getFormItemShow() {
    return api.get('/api/user/info/form/show', null, {
      // mock: true,
    })
  },

  // 获取协议详情
  async getAgreementInfo(id:string, productId?: number | null, serialNo?: string | null, options?: AgreementInfoOptions) {
    const res = await api.get<AgreementInfoResponse>(`/api/user/info/protocol/${id}`, {
      productId: productId ? productId : '',
      serialNo
    }, {
      // mock: true,
    })
    if (!res) {
      return res
    }

    return {
      ...res,
      content: options?.isMainProtocol ? transformAgreementContent(res.content) : res.content
    }
  },

  // 获取用户签署协议的记录
  getAgreementLogInfo(ids:any) {
    return api.get(`/api/user/info/log?${ids}`, null, {
      // mock: true,
    })
  },

  // 上游渠道联登处理 - 检查手机号是否登录
  checkMobileLogin(body:any) {
    return api.post('/api/upStreamUnionLogin/checkMobileLogin', body, {
      // mock: true,
    })
  },

  // 统计表单用户行为
  reportUserBehavior(type:any) {
    return api.post<UserInfo>(`/api/report/type?type=${type}`, null, {
      // mock: true,
      isCancelRequest: false
    })
  },

  // 统计匹配结果页产品列表项的用户点击
  reportUserProductClick(type:any) {
    return api.post<UserInfo>(`/api/report/click?type=${type}`, null, {
      // mock: true,
    })
  },

  // 上报H5手机设备(安卓-IOS)
  reportPhoneType(devicePlatForm:any) {
    return api.put(`/api/report/h5device?devicePlatForm=${devicePlatForm}`, null, {
      // mock: true,
    })
  },

  // 首页列表
  homeProductList(body:any) {
    return api.get<ColumnRecomenedProductList>('/api/product/columnRecommendProductList', body, {
      // mock: true,
    })
  },
  // 发起产品申请
  applyFor(body:ApplyParams) {
    return api.post<ApplyResponse>('/api/product/apply', body, {
      // mock: true,
    })
  },
  // 顶部推荐banner
  topRecommend() {
    return api.get<TopRecommentdProductItem[]>('/api/product/topRecommend', null, {
      // mock: true,
    })
  },
  // 上报产品曝光（不鉴权）
  // reportProductExposure(body:ReportProductExposureParams) {
  //   return api.post('/api/reportProductExposure', body, {
  //     // mock: true,
  //   })
  // },

  // CRM账号认证 - 废弃 - 2024-12-10
  // authCrmAccount(body = {}) {
  //   return api.put<UserInfo>('/api/admin/third/wechat/bindOpenid', body, {
  //     // mock: true,
  //   })
  // },

  // crm账号认证新版
  authCrmAccount(body = {}) {
    return api.put<UserInfo>('/api/crm/admin/org/employee/wechat/bindOpenid', body, {
      // mock: true,
    })
  },

  // 第三方H5产品联登授权
  authH5Product(body = {}) {
    return api.get<string>('/api/product/h5product/union', body, {
      // mock: true,
    })
  },

  // 账号注销验证码
  logOutVerifyCode(body = {}) {
    return api.post('/api/cancellation/sendPhoneVerifyCode', body, {
      // mock: true,
    })
  },

  // 账号注销
  cancellation(body = {}) {
    return api.post<any>('/api/cancellation', body, {
      // mock: true,
    })
  },

  // 退出登录
  logout(body = {}) {
    return api.post<any>('/api/logout', body, {
      // mock: true,
    })
  },

  // 事件上报接口
  reportEventData(body:any) {
    return api.post<UserInfo>('/api/report/event', body, {
      // mock: true,
      isCancelRequest: false
    })
  },
  // 根据code获取页面配置
  getPageConfigByCode(body:any) {
    return api.get<any>('/api/pageConfig/getPageConfigByCode', body, {
      // mock: true,
    })
  },
  // 统计api 转 H5类型产品跳转
  apiH5JumpUrlSuccessFlag(body:any) {
    return api.post<any>('/api/product/apiH5JumpUrlSuccessFlag', body, {
      // mock: true,
      isCancelRequest: false
    })
  },
  // 获取app首页icon列表
  getAppIndexIconList() {
    return api.get<any>('/api/pageConfig/getAppIndexIconList', null, {
      // mock: true,
    })
  },
  // 首页弹窗
  getHomeDialogBanner(body:any) {
    return api.get<any>('/api/banner/homeDialogBanner', body, {
      // mock: true,
    })
  },
  // 前端日志上报
  reportLog(reportParam:any) {
    return api.post<UserInfo>(`/api/report/log?content=${reportParam}`, null, {
      // mock: true,
      isCancelRequest: false
    })
  },
  // 获取上游协议详情
  getProtocolContent(query: getProtocolContentReq) {
    return api.get('/api/user/info/protocol2/getContent', query, {
      // mock: true,
    })
  },

  // 获取登录日志
  getInformationProtocolById(uid:string) {
    return api.get<LoginInfo>(`/api/getUserLoginVO?uid=${uid}`, null, {
      // mock: true,
    })
  },

  // 使用路由码获取渠道码
  getAdChannelCodeByRouteCode(routeCode: string) {
    return api.get<getAdChannelCodeByRouteCodeRes>(`/api/adChannel/exchange/adChannelCode?adRouteCode=${routeCode}`, null, {
      // mock: true
    })
  },

  // 获取手机号前三后四
  getPhoneNumByRSADecrypt(encryptionMobile:string, source:number, channelCode?:string) {
    return api.get<string>(`/api/getEncryptionMobile?encryptionMobile=${encryptionMobile}&source=${source}&channelCode=${channelCode}`, null, {
      // mock: true
      showError: false
    })
  },

  // 上报导流数据给crm（不鉴权）
  reportDiversion(body: ReportCrmDataReq) {
    return api.post('/api/reportDiversion', body, {
      // mock:true
    })
  },

  // 协议查询
  getByProtocolTypeAndLinkShow(query: GetByProtocolTypeAndLinkShowReq) {
    return api.get<GetByProtocolTypeAndLinkShowRes>('/api/user/info/protocol/getByProtocolTypeAndLinkShow', query, {
      //mock:true
    })
  },

  // CRM客户信息新增
  crmAddCustomer(body: CrmAddCustomerReq) {
    return api.post('/api/intendCustomer/addCustomer', body, {
      //mock:true
    })
  },

  // 登录后获取时间段导流最高价产品
  timePeriodDirectDrainageProduct(query: timePeriodDirectDrainageProductReq) {
    return api.get<timePeriodDirectDrainageProductRes>('/api/adChannel/timePeriodDirectDrainage/product', query, {
      //mock:true
    })
  },


}
export default indexApi

/** 接口装饰器 */
export const indexApiDecorator = {
  getAdChannelCodeByRouteCodeCache: decoratorAsyncCache(indexApi.getAdChannelCodeByRouteCode, 1000 * 20),
}
